<template>
	<v-card v-if="orderRow && cancelData && reasons">
		<v-card-title>
			<translate>Cancel Policy</translate>
		</v-card-title>
		<v-card-text>
			<v-form
				ref="form"
			>
				<v-row>
					<v-col
						class="py-3"
						cols="8"
						lg="5"
					>
						<v-text-field
							v-model="orderRow.productversion.product.name"
							readonly
							:label="$gettext('Insurance')"
							variant="outlined"
							hide-details
						/>
					</v-col>
					<v-col
						class="py-3"
						cols="6"
						lg="3"
					>
						<v-text-field
							v-model="startDate"
							readonly
							:label="$gettext('Start date')"
							variant="outlined"
							hide-details
						/>
					</v-col>
					<v-col
						class="py-3"
						cols="6"
						lg="3"
					>
						<v-text-field
							v-model="endDate"
							readonly
							:label="$gettext('End date')"
							variant="outlined"
							hide-details
						/>
					</v-col>
				</v-row>
				<v-row>
					<v-col>
						<h3><translate>Cancel (automatically filled in at +30 days)</translate></h3>
					</v-col>
				</v-row>
				<v-row>
					<v-col
						class="pb-0"
						cols="6"
						lg="3"
					>
						<date-picker
							v-model="cancelDate"
							outline
							name="Desired end date"
							:label="$gettext('Desired end date')"
							:min="cancelData.minDate"
							:max="cancelData.maxDate"
						/>
					</v-col>
				</v-row>
				<v-row no-gutters>
					<v-col class="py-0">
						<!-- eslint-disable-next-line vue/no-v-html value not injected, so it's safe-->
						<p v-html="$gettext('This date is <em\>to\<\/em\>, not <em\>Up to and Including\<\/em\>.')" />
					</v-col>
				</v-row>
				<v-row>
					<v-col>
						<v-divider />
					</v-col>
				</v-row>
				<v-row>
					<v-col>
						<h3><translate>Reason for cancellation</translate>:</h3>
					</v-col>
				</v-row>
				<v-row>
					<v-col class="py-0">
						<v-radio-group
							v-model="selectedReason"
							class="mb-0"
							:rules="[v => !!v || $gettext('Reason required')]"
							density="compact"
						>
							<div
								v-for="reason in reasons"
								:key="reason.id"
							>
								<v-radio
									:value="reason"
								>
									<template #label>
										<p
											class="mb-0"
										>
											<translate>{{ reason.name }}</translate>
										</p>
									</template>
								</v-radio>
								<v-text-field
									v-if="reason.other"
									v-model="reasonContext"
									:disabled="!selectedReason || selectedReason.name !== reason.name"
									class="pa-0 ma-0"
									variant="outlined"
									:rules="[v => (!selectedReason || !selectedReason.other || !!v) || $gettext('Reason required')]"
								/>
							</div>
						</v-radio-group>
					</v-col>
				</v-row>
				<v-row>
					<v-col class="py-0">
						<p><translate>The customer will automatically receive an e-mail with confirmation of this cancellation and a policy sheet with the end date stated on this.</translate></p>
					</v-col>
				</v-row>
			</v-form>
		</v-card-text>
		<v-card-actions>
			<v-spacer />
			<v-btn
				:loading="loading"
				:disabled="loading"
				color="primary"
				variant="text"
				@click="submit"
			>
				<translate>Confirm Cancellation</translate>
			</v-btn>
		</v-card-actions>
	</v-card>
</template>

<script>

import {
	axios,
} from '../../services/axios-service';
import {
	displayError, inform,
} from 'laravel-front-library';

export default {
	name: 'BackofficeCancel',

	data () {
		return {
			orderrowId: null,
			orderRow: null,
			cancelData: null,
			startDate: null,
			endDate: null,
			cancelDate: null,
			loading: false,
			reasons: null,
			selectedReason: null,
			reasonContext: ''
		};
	},

	mounted () {
		this.orderrowId = new URLSearchParams(window.location.search.substring(1)).get('id');
		this.alreadyDirectDebitted = (new Date()).getDate() >= 18;
		this.getOrderrowData();
	},

	methods: {
		/**
		 * Get the current order row, possible cancellation reasons, and the minimum, maximum, and suggested
		 * cancellation dates
		 *
		 * @returns {void}
		 */
		getOrderrowData () {
			axios.get(`/api/v0/orderrows/${this.orderrowId}`).then(({
				data: orderRow
			}) => {
				this.startDate = (new Date(orderRow.startDate.date)).toLocaleDateString('nl-NL');
				this.endDate = (new Date(orderRow.endDate.date)).toLocaleDateString('nl-NL');
				this.orderRow = orderRow;
			}).catch((error) => {
				window.captureException(error);
			});

			axios.get('/api/v0/orderrows/canceldata', {
				'params': {
					'id': this.orderrowId
				}
			}).then(({
				data: data
			}) => {
				this.cancelDate = data.suggestedDate;
				this.cancelData = data;
			}).catch((error) => {
				window.captureException(error);
			});

			axios.get('/api/v0/cancellationreasons', {
				params: {
					sortBy: 'id'
				}
			}).then(({
				data: {
					result
				}
			}) => {
				this.reasons = result;
				const index = this.reasons.findIndex((element) => element.other === true);

				if (index !== -1) {
					this.reasons.push(this.reasons.splice(index, 1)[0]);
				}
			}).catch((error) => {
				window.Sentry.captureException(error);
			});
		},

		/**
		 * Submit cancellation
		 *
		 * @returns {void}
		 */
		submit () {
			if (!this.$refs.form.validate()) {
				return;
			}

			this.loading = true;

			const postData = {
				'id': this.orderrowId,
				'endDate': this.cancelDate,
				'cancellationReasonId': this.selectedReason.id
			};

			if (this.selectedReason.other) {
				postData.cancellationContext = this.reasonContext;
			}

			axios.post('/api/v0/orderrowcancelevents', postData).then(() => {
				inform(this.$gettext('Cancellation successful'));
				setTimeout(() => {
					window.location.href = '/bo/orders/view?id=' + this.orderRow.orderId;
				}, 1000);
			}).catch((error) => {
				if (!error.response || error.response.status === 500) {
					if (error.message && error.message === 'Request aborted') {
						return;
					}
					displayError(
						this.$gettext('Error cancelling your policy') +
						': ' +
						this.$gettext('Please try again later')
					);
					window.captureException(error);
				} else {
					displayError(error.response.data);
				}
			}).finally(() => {
				this.loading = false;
			});
		}
	}
};
</script>
